import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Link,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';



const LoginModal = ({ open, onClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordSubmitted, setForgotPasswordSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  }

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  }

  const validateFields = () => {
    let isValid = true;
    
    // Validate email
    if (!email) {
      setEmailError('El campo es requerido.');
      isValid = false;
    }
    else if (email.length < 3) {
      setEmailError('El campo debe tener al menos 3 caracteres.');
      isValid = false;
    } else if (email.length > 100) {
      setEmailError('El campo no puede tener más de 100 caracteres.');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // Only validate password if not in forgot password mode
    if (!forgotPasswordMode) {
      if (!password) {
        setPasswordError('El campo es requerido.');
        isValid = false;
      } else if (password.length < 3) {
        setPasswordError('El campo debe tener al menos 3 caracteres.');
        isValid = false;
      } else if (password.length > 100) {
        setPasswordError('El campo no puede tener más de 100 caracteres.');
        isValid = false;
      } else {
        setPasswordError('');
      }
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate fields before submitting
    if (!validateFields()) {
      return;
    }
    
    if (forgotPasswordMode) {
      // Forgot password mode - call API to request password reset
      setLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/password-reset-request`,
          { email: email },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: false,
          }
        );
        
        if (response.status === 200) {
          setForgotPasswordSubmitted(true);
        } else {
          setError('Error al enviar la solicitud de recuperación de contraseña');
        }
      } catch (error) {
        // Always show success message to prevent email enumeration
        setForgotPasswordSubmitted(true);
      }
      setLoading(false);
      return;
    }
    
    setLoading(true);

    const result = await login(email, password, rememberMe);
    
    if (result.success) {
      onClose();
      setEmail('');
      setPassword('');
      setRememberMe(false);
      setEmailError('');
      setPasswordError('');
    } else {
      setError(result.error || 'Error al iniciar sesión');
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setRememberMe(false);
    setError('');
    setEmailError('');
    setPasswordError('');
    setForgotPasswordMode(false);
    setForgotPasswordSubmitted(false);
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#40b4ba',
          margin: { xs: 0, sm: '32px' },
          width: { xs: '100%', sm: '600px' },
          borderRadius: { xs: 0, sm: '8px' },
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            component="img"
            src="/assets/logo_white.png"
            alt="Logo"
            loading="lazy"
            decoding="async"
            sx={{ width: '150px' }}
          />
        </Box>
        <Typography 
          component={"div"}
          variant="h6" 
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontFamily: 'Avenir Heavy',
            textAlign: 'center',
            fontSize: '1.4em',
            mt: '20px',
          }}
        >
          {forgotPasswordMode ? 'Recuperar Contraseña' : 'Iniciar Sesión'}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ backgroundColor: '#40b4ba', px: { xs: 2, sm: 4 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {forgotPasswordSubmitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Si el correo existe en nuestra base de datos, recibirás un enlace para restablecer tu contraseña.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: 'white',  fontWeight: 'bold' }}>
                {forgotPasswordMode ? 'Correo electrónico' : 'Nombre de usuario o correo electrónico'}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                error={!!emailError}
                helperText={emailError}
                FormHelperTextProps={{
                  sx: { color: 'white', fontWeight: 'bold', mt: 0.5, mx: 0 }
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: emailError ? '#f44336' : 'white',
                    },
                    '&:hover fieldset': {
                      borderColor: emailError ? '#f44336' : 'white',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: emailError ? '#f44336' : 'white',
                    },
                  },
                }}
              />
              {!forgotPasswordMode && (
                <>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Contraseña
                  </Typography>
                  <TextField
                    margin="dense"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                    }}
                    error={!!passwordError}
                    helperText={passwordError}
                    FormHelperTextProps={{
                      sx: { color: 'white', fontWeight: 'bold', mt: 0.5, mx: 0 }
                    }}
                    InputProps={{ 
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ color: '#666' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ 
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: passwordError ? '#f44336' : 'white',
                        },
                        '&:hover fieldset': {
                          borderColor: passwordError ? '#f44336' : 'white',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: passwordError ? '#f44336' : 'white',
                        },
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          sx={{
                            color: 'white',
                            '&.Mui-checked': {
                              color: 'white',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8em', sm: '0.9em' } }}>
                          Recordarme
                        </Typography>
                      }
                    />
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setForgotPasswordMode(true);
                        setPassword('');
                        setPasswordError('');
                        setError('');
                        setShowPassword(false);
                      }}
                      sx={{
                        color: 'white',
                        textDecoration: 'none',
                        fontSize: { xs: '0.8em', sm: '0.9em' },
                        fontWeight: 'bold',
                        fontFamily: 'Avenir Regular',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#40b4ba', 
          justifyContent: 'center', 
          flexDirection: forgotPasswordMode && !forgotPasswordSubmitted ? 'row' : 'column',
          gap: forgotPasswordMode && !forgotPasswordSubmitted ? 2 : 0
        }}>
          {forgotPasswordMode && !forgotPasswordSubmitted && (
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setForgotPasswordMode(false);
                setEmail('');
                setEmailError('');
                setError('');
              }}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1em',
                fontFamily: 'Avenir Regular',
                mb: forgotPasswordMode ? 0 : 1,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Volver
            </Button>
          )}
          {!forgotPasswordSubmitted && (
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: '#eb947e',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1em',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#eb947e',
                  opacity: 0.9,
                },
                fontFamily: 'Avenir Regular',
                mb: forgotPasswordMode ? 0 : 1
              }}
              disabled={loading}
            >
              {forgotPasswordMode 
                ? (loading ? 'Enviando...' : 'Enviar instrucciones')
                : loading 
                  ? 'Iniciando sesión...' 
                  : 'Iniciar sesión'}
            </Button>
          )}
          {!forgotPasswordMode && (
            <Link
              component="button"
              type="button"
              onClick={() => {
                handleClose();
                navigate(process.env.REACT_APP_HOME_URL + '/formulario-de-registro');
              }}
              sx={{
                color: 'white',
                textDecoration: 'none',
                fontSize: '0.9em',
                fontWeight: 'bold',
                fontFamily: 'Avenir Regular',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
                my: 1,
                mr: "8px"
              }}
            >
              ¿No tienes cuenta?
            </Link>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LoginModal;
